import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FinancialService, { Payment, Invoice } from '../../../services/FinancialService';

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (payment: Payment) => void;
}

interface PaymentFormData {
  invoice_id: string;
  amount: string;
  payment_method: Payment['paymentMethod'];
  payment_date: Date;
  transaction_id: string;
  notes: string;
  status: Payment['status'];
}

const RecordPaymentDialog: React.FC<RecordPaymentDialogProps> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const [formData, setFormData] = useState<PaymentFormData>({
    invoice_id: '',
    amount: '',
    payment_method: 'bank_transfer',
    payment_date: new Date(),
    transaction_id: '',
    notes: '',
    status: 'completed',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadInvoices();
    }
  }, [open]);

  useEffect(() => {
    if (formData.invoice_id) {
      const invoice = invoices.find((inv) => inv.id === parseInt(formData.invoice_id));
      setSelectedInvoice(invoice || null);
      if (invoice && !formData.amount) {
        setFormData((prev) => ({ ...prev, amount: invoice.balanceDue.toString() }));
      }
    } else {
      setSelectedInvoice(null);
    }
  }, [formData.invoice_id, invoices]);

  const loadInvoices = async () => {
    setLoadingInvoices(true);
    setError(null);
    try {
      // Load invoices that have outstanding balance
      const { invoices: allInvoices } = await FinancialService.getInvoices({
        status: 'all',
        perPage: 100,
      });
      
      // Filter to only show invoices with balance due
      const unpaidInvoices = allInvoices.filter((inv) => inv.balanceDue > 0);
      setInvoices(unpaidInvoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleChange = (field: keyof PaymentFormData, value: string | Date) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.invoice_id) {
      errors.invoice_id = 'Please select an invoice';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Please enter a valid amount';
    }

    if (selectedInvoice && parseFloat(formData.amount) > selectedInvoice.balanceDue) {
      errors.amount = `Amount cannot exceed balance due ($${selectedInvoice.balanceDue.toFixed(2)})`;
    }

    if (!formData.payment_method) {
      errors.payment_method = 'Please select a payment method';
    }

    if (!formData.payment_date) {
      errors.payment_date = 'Please select a payment date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payment = await FinancialService.createPayment({
        invoice_id: parseInt(formData.invoice_id),
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        payment_date: formData.payment_date.toISOString().split('T')[0],
        transaction_id: formData.transaction_id || null,
        notes: formData.notes || null,
        status: formData.status,
      });

      onSuccess(payment);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        invoice_id: '',
        amount: '',
        payment_method: 'bank_transfer',
        payment_date: new Date(),
        transaction_id: '',
        notes: '',
        status: 'completed',
      });
      setFormErrors({});
      setError(null);
      setSelectedInvoice(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Payment</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingInvoices ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Invoice"
              value={formData.invoice_id}
              onChange={(e) => handleChange('invoice_id', e.target.value)}
              error={!!formErrors.invoice_id}
              helperText={formErrors.invoice_id}
              required
              fullWidth
            >
              {invoices.length === 0 ? (
                <MenuItem value="" disabled>
                  No invoices with outstanding balance
                </MenuItem>
              ) : (
                invoices.map((invoice) => (
                  <MenuItem key={invoice.id} value={invoice.id.toString()}>
                    {invoice.invoiceNumber} - {invoice.clientName} (Balance: ${invoice.balanceDue.toFixed(2)})
                  </MenuItem>
                ))
              )}
            </TextField>

            {selectedInvoice && (
              <Alert severity="info">
                Invoice Amount: ${selectedInvoice.amount.toFixed(2)} | Balance Due: $
                {selectedInvoice.balanceDue.toFixed(2)}
              </Alert>
            )}

            <TextField
              label="Payment Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              required
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{
                min: 0.01,
                step: 0.01,
              }}
            />

            <TextField
              select
              label="Payment Method"
              value={formData.payment_method}
              onChange={(e) => handleChange('payment_method', e.target.value)}
              error={!!formErrors.payment_method}
              helperText={formErrors.payment_method}
              required
              fullWidth
            >
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="credit_card">Credit Card</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="ach">ACH</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Payment Date"
                value={formData.payment_date}
                onChange={(date) => handleChange('payment_date', date || new Date())}
                slotProps={{
                  textField: {
                    required: true,
                    fullWidth: true,
                    error: !!formErrors.payment_date,
                    helperText: formErrors.payment_date,
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Transaction ID"
              value={formData.transaction_id}
              onChange={(e) => handleChange('transaction_id', e.target.value)}
              fullWidth
              placeholder="Optional"
            />

            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              required
              fullWidth
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </TextField>

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Optional notes about this payment"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading || loadingInvoices}>
          {loading ? <CircularProgress size={24} /> : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
